import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  /**
   * Renders the back button at the top-left and centres the title — the one
   * layout every page that leads back somewhere uses, so the button sits in the
   * same place with the same look on all of them.
   */
  onBack?: () => void;
}

export function PageHeader({ title, subtitle, action, onBack }: PageHeaderProps) {
  const { t } = useTranslation('common');

  if (onBack) {
    return (
      <div className="relative flex flex-col items-center px-11 text-center">
        <button
          type="button"
          onClick={onBack}
          aria-label={t('common.back')}
          className="absolute left-0 top-0.5 grid size-9 place-items-center rounded-xl bg-[var(--primary)] text-[var(--primary-on)] transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]"
        >
          <ChevronLeft size={18} aria-hidden />
        </button>
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[var(--text-primary)]">
          {title}
        </h1>
        {subtitle && (
          <p className="text-[13px] text-[var(--text-secondary)] mt-1 max-w-md">{subtitle}</p>
        )}
        {action && <div className="absolute right-0 top-0.5">{action}</div>}
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[var(--text-primary)]">
          {title}
        </h1>
        {subtitle && (
          <p className="text-[13px] text-[var(--text-secondary)] mt-1">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}
